import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { AreaChart, Area, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid } from 'recharts';
import {
  ChartLineUp, Eye, Users, CursorClick, ArrowBendDownRight, Clock,
  ArrowUp, ArrowDown, CaretLeft, Broadcast, Globe, Browser, DeviceMobile, MapPin
} from '@phosphor-icons/react';
import TrafficAlerts from './TrafficAlerts';

const RANGES = [
  { key: '24h', label: '24H' },
  { key: '7d', label: '7D' },
  { key: '30d', label: '30D' },
  { key: '90d', label: '90D' },
];

const fmt = (n) => {
  if (n === null || n === undefined) return '0';
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K';
  return String(Math.round(n));
};

const pctChange = (cur, prev) => {
  if (!prev) return cur > 0 ? 100 : 0;
  return Math.round(((cur - prev) / prev) * 100);
};

const Delta = ({ cur, prev, invert }) => {
  const p = pctChange(cur, prev);
  if (p === 0) return <span className="text-slate-500 text-xs font-mono">—</span>;
  const good = invert ? p < 0 : p > 0;
  return (
    <span className={`flex items-center gap-0.5 text-xs font-mono font-bold ${good ? 'text-[#39FF14]' : 'text-red-400'}`}>
      {p > 0 ? <ArrowUp size={11} weight="bold" /> : <ArrowDown size={11} weight="bold" />}{Math.abs(p)}%
    </span>
  );
};

const Kpi = ({ icon: Icon, label, value, cur, prev, invert, accent }) => (
  <div data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`} className="bg-card border border-white/10 p-5 relative overflow-hidden group hover:border-white/25 transition-colors">
    <div className="absolute -right-4 -top-4 opacity-[0.06] group-hover:opacity-[0.1] transition-opacity">
      <Icon size={90} weight="fill" />
    </div>
    <div className="flex items-center gap-2 mb-3">
      <Icon size={16} style={{ color: accent }} weight="bold" />
      <span className="text-[11px] uppercase tracking-widest text-slate-400 font-bold">{label}</span>
    </div>
    <div className="flex items-end justify-between">
      <span className="text-3xl font-black text-white tracking-tight">{value}</span>
      <Delta cur={cur} prev={prev} invert={invert} />
    </div>
  </div>
);

const MetricPanel = ({ title, icon: Icon, rows, labelKey }) => {
  const max = Math.max(1, ...rows.map(r => r.y || 0));
  return (
    <div className="bg-card border border-white/10 p-5">
      <div className="flex items-center gap-2 mb-4">
        <Icon size={16} className="text-primary" weight="bold" />
        <h3 className="text-sm uppercase tracking-widest font-bold text-slate-200">{title}</h3>
      </div>
      {rows.length === 0 ? (
        <p className="text-slate-600 text-xs font-mono py-4 text-center">No data yet</p>
      ) : (
        <div className="space-y-2">
          {rows.slice(0, 8).map((r, i) => (
            <div key={i} className="relative">
              <div className="absolute inset-0 bg-primary/10" style={{ width: `${((r.y || 0) / max) * 100}%` }} />
              <div className="relative flex justify-between items-center px-2 py-1.5 text-xs">
                <span className="truncate text-slate-300 font-mono max-w-[75%]" title={r.x || '(none)'}>{r.x || '(none)'}</span>
                <span className="text-white font-bold">{fmt(r.y)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Analytics = () => {
  const navigate = useNavigate();
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState('');
  const [range, setRange] = useState('7d');
  const [summary, setSummary] = useState(null);
  const [series, setSeries] = useState([]);
  const [metrics, setMetrics] = useState({ url: [], referrer: [], browser: [], os: [], device: [], country: [] });
  const [loading, setLoading] = useState(false);
  const [liveActive, setLiveActive] = useState(null);

  useEffect(() => {
    axios.get('/api/analytics/websites', { withCredentials: true })
      .then(res => {
        setSites(res.data);
        if (res.data.length) setSiteId(res.data[0].id);
      })
      .catch(() => toast.error('Failed to load websites'));
  }, []);

  // Real-time "online now" for the selected site (poll every 5s)
  useEffect(() => {
    if (!siteId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await axios.get(`/api/analytics/active?website_id=${siteId}`, { withCredentials: true });
        if (!cancelled) setLiveActive(res.data.active);
      } catch (e) { /* keep last */ }
    };
    setLiveActive(null);
    tick();
    const iv = setInterval(tick, 5000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [siteId]);

  const loadData = useCallback(async () => {
    if (!siteId) return;
    setLoading(true);
    try {
      const [sum, pv, murl, mref, mbrow, mos, mdev, mctry] = await Promise.all([
        axios.get(`/api/analytics/summary?website_id=${siteId}&range=${range}`, { withCredentials: true }),
        axios.get(`/api/analytics/pageviews?website_id=${siteId}&range=${range}`, { withCredentials: true }),
        axios.get(`/api/analytics/metrics?website_id=${siteId}&type=url&range=${range}`, { withCredentials: true }),
        axios.get(`/api/analytics/metrics?website_id=${siteId}&type=referrer&range=${range}`, { withCredentials: true }),
        axios.get(`/api/analytics/metrics?website_id=${siteId}&type=browser&range=${range}`, { withCredentials: true }),
        axios.get(`/api/analytics/metrics?website_id=${siteId}&type=os&range=${range}`, { withCredentials: true }),
        axios.get(`/api/analytics/metrics?website_id=${siteId}&type=device&range=${range}`, { withCredentials: true }),
        axios.get(`/api/analytics/metrics?website_id=${siteId}&type=country&range=${range}`, { withCredentials: true }),
      ]);
      setSummary(sum.data);
      const views = pv.data.pageviews || [];
      const sessions = pv.data.sessions || [];
      const merged = views.map((v, i) => ({
        t: v.x, views: v.y, sessions: sessions[i]?.y || 0,
      }));
      setSeries(merged);
      setMetrics({ url: murl.data, referrer: mref.data, browser: mbrow.data, os: mos.data, device: mdev.data, country: mctry.data });
    } catch (e) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [siteId, range]);

  useEffect(() => { loadData(); }, [loadData]);

  const st = summary?.stats || {};
  const cmp = st.comparison || {};
  const avgTime = st.visits ? Math.round((st.totaltime || 0) / st.visits) : 0;
  const avgTimePrev = cmp.visits ? Math.round((cmp.totaltime || 0) / cmp.visits) : 0;
  const bounceRate = st.visits ? Math.round(((st.bounces || 0) / st.visits) * 100) : 0;
  const bouncePrev = cmp.visits ? Math.round(((cmp.bounces || 0) / cmp.visits) * 100) : 0;

  const tickFmt = (t) => {
    const d = new Date(t);
    return range === '24h' ? `${d.getHours()}:00` : `${d.getMonth() + 1}/${d.getDate()}`;
  };

  return (
    <div className="min-h-screen p-6 lg:p-10 max-w-7xl mx-auto animate-in fade-in duration-500">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <button onClick={() => navigate('/')} data-testid="analytics-back-btn" className="flex items-center gap-1 text-slate-500 hover:text-white text-xs font-mono uppercase tracking-widest mb-3 transition-colors">
            <CaretLeft size={14} /> Launchpad
          </button>
          <h1 className="text-4xl font-black tracking-tighter text-white flex items-center gap-3">
            <ChartLineUp size={36} className="text-primary" weight="bold" /> ANALYTICS
          </h1>
          <p className="text-slate-500 font-mono text-xs uppercase tracking-widest mt-2">Powered by Umami · Privacy-first</p>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          {(liveActive !== null || summary) && (
            <div className="flex items-center gap-2 bg-[#39FF14]/10 border border-[#39FF14]/40 px-3 py-2" data-testid="analytics-active">
              <Broadcast size={16} className="text-[#39FF14] animate-pulse" weight="fill" />
              <span className="text-[#39FF14] font-bold text-sm">{liveActive !== null ? liveActive : (summary?.active ?? 0)}</span>
              <span className="text-slate-400 text-[11px] uppercase tracking-wider">online now</span>
            </div>
          )}
          <select
            data-testid="analytics-site-select"
            value={siteId}
            onChange={e => setSiteId(e.target.value)}
            className="bg-card border border-white/15 px-4 py-2 text-white text-sm focus:outline-none focus:border-primary min-w-[180px]"
          >
            {sites.map(s => <option key={s.id} value={s.id}>{s.name} ({s.domain})</option>)}
          </select>
        </div>
      </div>

      {/* Range selector */}
      <div className="flex gap-1 mb-6 bg-card border border-white/10 p-1 w-fit">
        {RANGES.map(r => (
          <button
            key={r.key}
            data-testid={`range-${r.key}`}
            onClick={() => setRange(r.key)}
            className={`px-4 py-1.5 text-xs font-bold uppercase tracking-widest transition-colors ${range === r.key ? 'bg-primary text-black' : 'text-slate-400 hover:text-white'}`}
          >
            {r.label}
          </button>
        ))}
      </div>

      {sites.length === 0 ? (
        <div className="bg-card border border-white/10 p-16 text-center">
          <Globe size={48} className="text-slate-600 mx-auto mb-4" />
          <p className="text-slate-400 font-mono">No websites found in your Umami account.</p>
        </div>
      ) : (
        <div className={loading ? 'opacity-50 pointer-events-none transition-opacity' : 'transition-opacity'}>
          {/* Traffic Alerts */}
          <TrafficAlerts sites={sites} />

          {/* KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            <Kpi icon={Eye} label="Views" value={fmt(st.pageviews)} cur={st.pageviews || 0} prev={cmp.pageviews || 0} accent="#00E5FF" />
            <Kpi icon={Users} label="Visitors" value={fmt(st.visitors)} cur={st.visitors || 0} prev={cmp.visitors || 0} accent="#8b60ed" />
            <Kpi icon={CursorClick} label="Visits" value={fmt(st.visits)} cur={st.visits || 0} prev={cmp.visits || 0} accent="#0078D4" />
            <Kpi icon={ArrowBendDownRight} label="Bounce Rate" value={`${bounceRate}%`} cur={bounceRate} prev={bouncePrev} invert accent="#FF6B6B" />
            <Kpi icon={Clock} label="Avg Time" value={`${avgTime}s`} cur={avgTime} prev={avgTimePrev} accent="#39FF14" />
          </div>

          {/* Chart */}
          <div className="bg-card border border-white/10 p-5 mb-6">
            <h3 className="text-sm uppercase tracking-widest font-bold text-slate-200 mb-4">Traffic Over Time</h3>
            <div className="h-[280px]" data-testid="analytics-chart">
              {series.length === 0 ? (
                <div className="h-full flex items-center justify-center text-slate-600 font-mono text-sm">No traffic in this period</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={series} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gViews" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#00E5FF" stopOpacity={0.5} />
                        <stop offset="100%" stopColor="#00E5FF" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="gSess" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#8b60ed" stopOpacity={0.4} />
                        <stop offset="100%" stopColor="#8b60ed" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" vertical={false} />
                    <XAxis dataKey="t" tickFormatter={tickFmt} stroke="#64748b" fontSize={11} tickLine={false} />
                    <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{ background: '#0A0B0E', border: '1px solid #ffffff20', borderRadius: 0, fontSize: 12 }}
                      labelStyle={{ color: '#94a3b8' }}
                      labelFormatter={(t) => new Date(t).toLocaleString()}
                    />
                    <Area type="monotone" dataKey="views" name="Views" stroke="#00E5FF" strokeWidth={2} fill="url(#gViews)" />
                    <Area type="monotone" dataKey="sessions" name="Sessions" stroke="#8b60ed" strokeWidth={2} fill="url(#gSess)" />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Metrics grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <MetricPanel title="Top Pages" icon={ChartLineUp} rows={metrics.url} />
            <MetricPanel title="Referrers" icon={ArrowBendDownRight} rows={metrics.referrer} />
            <MetricPanel title="Countries" icon={MapPin} rows={metrics.country} />
            <MetricPanel title="Browsers" icon={Browser} rows={metrics.browser} />
            <MetricPanel title="Operating Systems" icon={Globe} rows={metrics.os} />
            <MetricPanel title="Devices" icon={DeviceMobile} rows={metrics.device} />
          </div>
        </div>
      )}
    </div>
  );
};

export default Analytics;
